// HQ alerts: what VERANeural HQ used to email the team (releases, merged pull requests, health checks, notes, the morning
// "due soon", the Friday report, team requests and support messages) is now an alert in Activity, sorted by kind.
// They are the records of HQ's Events list (the server writes them; see HqAlerts on the control plane). Which ones a
// person has seen is remembered in this browser only (a convenience; nothing is lost if it's cleared).

import { useCallback, useEffect, useState } from "react";
import { crm, type CrmRecord } from "./crm";
import { auth } from "../auth/auth";

export type AlertTab = "all" | "releases" | "prs" | "health" | "notes" | "changes";
export const ALERT_TABS: { id: AlertTab; label: string }[] = [
  { id: "all", label: "All" }, { id: "releases", label: "Releases" }, { id: "prs", label: "Pull requests" },
  { id: "health", label: "Health checks" }, { id: "notes", label: "Notes & requests" }, { id: "changes", label: "Changes" },
];

/** The tab an alert's kind belongs to. */
export function tabOf(kind: string | undefined): Exclude<AlertTab, "all" | "changes"> {
  if (!kind) return "notes";
  if (kind.startsWith("Release")) return "releases";
  if (kind === "PR merged") return "prs";
  if (kind.startsWith("Watcher")) return "health";
  return "notes";
}

/** Bad news gets the warning colour: a failed release, a rollback, a failing watch. */
export const isBad = (kind: string | undefined) => kind === "Release failed" || kind === "Release rolled back" || kind === "Watcher failing";

export type Alert = { id: string; kind: string; title: string; text?: string; link?: string; at: number; tab: ReturnType<typeof tabOf> };

const str = (r: CrmRecord, f: string) => (typeof r.values[f] === "string" ? (r.values[f] as string) : undefined);
export function toAlert(r: CrmRecord): Alert {
  const kind = str(r, "kind") ?? "Note";
  const when = str(r, "at");
  const at = when ? Date.parse(when) : r.created;
  return { id: r.id, kind, title: str(r, "title") ?? kind, text: str(r, "notes"), link: str(r, "link"), at: Number.isFinite(at) ? at : r.created, tab: tabOf(kind) };
}

const SEEN = "vera.alerts.seen";
export const lastSeen = () => { try { return Number(localStorage.getItem(SEEN) ?? 0) || 0; } catch { return 0; } };
export const markSeen = (at = Date.now()) => { try { localStorage.setItem(SEEN, String(at)); } catch { /* private window */ } window.dispatchEvent(new Event(SEEN)); };

/** The team's alerts, newest first, and how many arrived since this browser last opened Activity. Off for companies. */
export function useAlerts(on: boolean) {
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [seen, setSeen] = useState(lastSeen);
  const load = useCallback(async () => {
    if (!on) { setAlerts([]); return; }
    try { setAlerts((await crm.list(() => auth.getToken(), "events")).map(toAlert).sort((a, b) => b.at - a.at)); }
    catch { setAlerts([]); }
  }, [on]);
  useEffect(() => {
    void load();
    if (!on) return;
    const every = window.setInterval(() => void load(), 5 * 60_000);
    const onSeen = () => setSeen(lastSeen());
    window.addEventListener(SEEN, onSeen);
    return () => { window.clearInterval(every); window.removeEventListener(SEEN, onSeen); };
  }, [load, on]);
  const unread = (alerts ?? []).filter((a) => a.at > seen).length;
  return { alerts, unread, seen, reload: load };
}
